import crypto from 'crypto'

// AES-256-CTR body/response envelope { iv, content } (hex), the format the goservice backends'
// CryptoService reads and writes. The key is chosen per request (see cryptoKeys.ts).
export interface CtrEnvelope {
    iv: string
    content: string
}

export function ctrEncrypt(key: Buffer, text: string): CtrEnvelope {
    const iv = crypto.randomBytes(16)
    const cipher = crypto.createCipheriv('aes-256-ctr', key, iv)
    const content = Buffer.concat([cipher.update(Buffer.from(text, 'utf8')), cipher.final()])
    return { iv: iv.toString('hex'), content: content.toString('hex') }
}

export function ctrDecrypt(key: Buffer, envelope: CtrEnvelope): string {
    const decipher = crypto.createDecipheriv('aes-256-ctr', key, Buffer.from(envelope.iv, 'hex'))
    return Buffer.concat([decipher.update(Buffer.from(envelope.content, 'hex')), decipher.final()]).toString('utf8')
}

// Page payloads also have a `content` (an array), so both fields must be strings.
export function isCtrEnvelope(value: any): boolean {
    return !!value && typeof value === 'object' && typeof value.iv === 'string' && typeof value.content === 'string'
}

// REACT_APP_AUTH_PUBLIC_KEY is base64 SPKI DER on one line (dotenv cannot hold a multi-line PEM).
export function publicKeyPem(base64Der: string): string {
    const body = (base64Der.replace(/\s+/g, '').match(/.{1,64}/g) || []).join('\n')
    return '-----BEGIN PUBLIC KEY-----\n' + body + '\n-----END PUBLIC KEY-----\n'
}

// RSA_PKCS1_OAEP_PADDING, as a number: the browser polyfill has no crypto.constants.
const OAEP_PADDING = 4

// Before login there is no session key: wrap a fresh one-time key with auth's public key. Only auth
// holds the private key, so the request and its response stay readable to auth and this page only.
export function newEnvelope(publicKeyBase64: string | undefined = process.env.REACT_APP_AUTH_PUBLIC_KEY): { key: Buffer, header: string } {
    if (!publicKeyBase64) throw new Error('REACT_APP_AUTH_PUBLIC_KEY is not set')
    const key = crypto.randomBytes(32)
    const wrapped = crypto.publicEncrypt({ key: publicKeyPem(publicKeyBase64), padding: OAEP_PADDING }, key)
    return { key, header: wrapped.toString('base64') }
}
